var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Sutl } from '../../../../sutl';
import { Timeouts } from '../framework/Timeouts';
import { escape, floatRegExp, unescape, } from '../SoundStructure';
class Preset {
    constructor(name) {
        this.name = name;
    }
}
class Channel {
    constructor(name) {
        this.name = name;
        this.fader = 0;
        this.mute = false;
        this.phoneConnect = false;
        this.voipCallAppearanceState = 'free';
        this.voipCallAppearanceInfo = ['', ''];
        this.voipLineState = 'line_registered';
    }
}
class VirtualChannel extends Channel {
    constructor(name, virtualChannelType, physicalChannelType, physicalChannelIndices, mute = false) {
        super(name);
        this.virtualChannelType = virtualChannelType;
        this.physicalChannelType = physicalChannelType;
        this.mute = mute;
        if (Array.isArray(physicalChannelIndices)) {
            this.physicalChannelIndices = physicalChannelIndices;
        }
        else {
            this.physicalChannelIndices = [physicalChannelIndices];
        }
    }
}
class VirtualChannelGroup extends Channel {
    constructor(name, virtualChannels) {
        super(name);
        this.virtualChannels = virtualChannels;
    }
}
function isVirtualChannel(channel) {
    return 'virtualChannelType' in channel;
}
function isVirtualChannelGroup(channel) {
    return !('virtualChannelType' in channel);
}
export class SoundStructure {
    constructor(socket, log) {
        this.socket = socket;
        this.channels = [
            new VirtualChannel('Amplifier', 'mono', 'cr_line_out', 1),
            new VirtualChannel('Assistive Listening', 'mono', 'cr_line_out', 2),
            new VirtualChannel('Audio Input A', 'mono', 'cr_mic_in', 1, true),
            new VirtualChannel('Audio Input B', 'mono', 'cr_mic_in', 2, true),
            new VirtualChannel('Audio Input C', 'mono', 'cr_mic_in', 3, true),
            new VirtualChannel('Codec Line Mix Out', 'stereo', 'clink_aux_out', [3, 4]),
            new VirtualChannel('Codec Program In', 'stereo', 'clink_aux_in', [3, 4]),
            new VirtualChannel('Codec Stereo Mics Out', 'stereo', 'clink_aux_out', [1, 2]),
            new VirtualChannel('Codec UI Audio In', 'stereo', 'clink_raw_in', [1, 2]),
            new VirtualChannel('Codec Video Call In', 'stereo', 'clink_aux_in', [1, 2]),
            new VirtualChannel('Codec Voice Call In', 'mono', 'clink_raw_in', 3),
            new VirtualChannel('Codec Voice Call Mix Out', 'mono', 'clink_aux_out', 6),
            new VirtualChannel('Overflow Room A', 'mono', 'cr_line_out', 3),
            new VirtualChannel('Overflow Room B', 'mono', 'cr_line_out', 4),
            new VirtualChannel('Program Audio', 'mono', 'cr_mic_in', 4),
            new VirtualChannel('Sig Gen', 'mono', 'sig_gen', 1),
            new VirtualChannel('Sound Reinforcement', 'mono', 'submix', 1),
            new VirtualChannel('Sound Reinforcement Mute Status', 'control', 'digital_gpio_out', 1),
            new VirtualChannel('Table Mic A', 'mono', 'cr_mic_in', 6),
            new VirtualChannel('Table Mic B', 'mono', 'cr_mic_in', 7),
            new VirtualChannel('Table Mic C', 'mono', 'cr_mic_in', 8),
            new VirtualChannel('VoIP In', 'mono', 'voip_in', 1),
            new VirtualChannel('VoIP Out', 'mono', 'voip_out', 1),
            new VirtualChannelGroup('Mics', ['Table Mic A', 'Table Mic B', 'Table Mic C']),
            new VirtualChannelGroup('Polycom Video Codec In', [
                'Codec Program In',
                'Codec UI Audio In',
                'Codec Video Call In',
                'Codec Voice Call In',
            ]),
            new VirtualChannelGroup('Polycom Video Codec Out', [
                'Codec Line Mix Out',
                'Codec Stereo Mics Out',
                'Codec Voice Call Mix Out',
            ]),
        ];
        this.presets = [
            new Preset('Mics Mute Preset'),
            new Preset('Mics Unmute Preset'),
            new Preset('Sound Reinforcement Mute Preset'),
            new Preset('Sound Reinforcement Unmute Preset'),
        ];
        this.timeouts = new Timeouts();
        this.asyncHandlers = [
            {
                regExp: /^authenticate "(.+)" "(.+)"/,
                callback: (match) => {
                    if (match[1] === 'admin') {
                        this.socket.send('authenticated');
                    }
                    else {
                        this.socket.send('error "authentication failed"');
                    }
                },
            },
            {
                regExp: /^get fader "(.+)"/,
                callback: (match) => {
                    const chan = this.getChannel(unescape(match[1]));
                    if (chan === undefined) {
                        this.socket.send('error "no virtual channel or virtual channel group with that label exists"');
                    }
                    else {
                        this.socket.send(`val fader "${match[1]}" ${chan.fader.toFixed(1)}`);
                    }
                },
            },
            {
                regExp: new RegExp(`^set fader "(.+)" (${floatRegExp})`),
                callback: (match) => {
                    const chan = this.getChannel(unescape(match[1]));
                    if (chan === undefined) {
                        this.socket.send('error "no virtual channel or virtual channel group with that label exists"');
                    }
                    else {
                        chan.fader = parseFloat(match[2]);
                        this.socket.send(`val fader "${match[1]}" ${chan.fader.toFixed(1)}`);
                    }
                },
            },
            {
                regExp: /^get dev_firmware_ver (\d+)/,
                callback: (match) => {
                    this.socket.send(`val dev_firmware_ver ${match[1]} "1.7.8"`);
                },
            },
            {
                regExp: /^set dev_led_cycle (\d+) (\d+)/,
                callback: (match) => {
                    this.socket.send(`val dev_led_cycle ${match[1]} ${match[2]}`);
                },
            },
            {
                regExp: /^get (mute|digital_gpio_state) "(.+)"/,
                callback: (match) => {
                    const chan = this.getChannel(unescape(match[2]));
                    if (chan === undefined) {
                        this.socket.send('error "no virtual channel or virtual channel group with that label exists"');
                    }
                    else {
                        this.socket.send(`val ${match[1]} "${match[2]}" ${chan.mute ? '1' : '0'}`);
                    }
                },
            },
            {
                regExp: /^set (mute|digital_gpio_state) "(.+)" (0|1)/,
                callback: (match) => {
                    const chan = this.getChannel(unescape(match[2]));
                    if (chan === undefined) {
                        this.socket.send('error "no virtual channel or virtual channel group with that label exists"');
                    }
                    else {
                        chan.mute = match[3] === '1';
                        this.socket.send(`val ${match[1]} "${match[2]}" ${chan.mute ? '1' : '0'}`);
                    }
                },
            },
            {
                regExp: /^set phone_connect "(.+)" (0|1)/,
                callback: (match) => __awaiter(this, void 0, void 0, function* () {
                    const name = unescape(match[1]);
                    const vc = this.getVirtualChannel(name, ['pstn_out', 'voip_out']);
                    if (vc === undefined)
                        return;
                    vc.phoneConnect = match[2] === '1';
                    this.socket.send(`val phone_connect "${match[1]}" ${match[2]}`);
                    if (vc.physicalChannelType === 'pstn_out')
                        return;
                    if (vc.phoneConnect) {
                        vc.voipCallAppearanceState = 'proceeding';
                        const info = escape(vc.voipCallAppearanceInfo[0]);
                        yield this.timeouts.delay(1000);
                        this.socket.send(`val voip_call_appearance_state "${match[1]}" 1 proceeding`);
                        this.socket.send(`val voip_call_appearance_info "${match[1]}" 1 1 "${info}"`);
                        yield this.timeouts.delay(1000);
                        vc.voipCallAppearanceState = 'connected';
                        this.socket.send(`val voip_call_appearance_state "${match[1]}" 1 connected`);
                    }
                    else {
                        vc.voipCallAppearanceState = 'disconnected';
                        this.socket.send(`val voip_call_appearance_state "${match[1]}" 1 disconnected`);
                    }
                }),
            },
            {
                regExp: /^set phone_dial "(.+)" "(.+)"/,
                callback: (match) => {
                    const name = unescape(match[1]);
                    const vc = this.getVirtualChannel(name, ['pstn_out', 'voip_out']);
                    if (vc === undefined)
                        return;
                    this.socket.send(`val phone_dial "${match[1]}" "${match[2]}"`);
                },
            },
            {
                regExp: /^set phone_reject "(.+)"/,
                callback: (match) => {
                    const name = unescape(match[1]);
                    const vc = this.getVirtualChannel(name, ['pstn_out', 'voip_out']);
                    if (vc === undefined)
                        return;
                    this.socket.send(`val phone_reject "${match[1]}"`);
                },
            },
            {
                regExp: /^get sys_name/,
                callback: (match) => {
                    this.socket.send(`val sys_name "SoundStructure System"`);
                },
            },
            {
                regExp: /^ping/,
                callback: (match) => {
                    this.socket.send('pong');
                },
            },
            {
                regExp: /^presetlist/,
                callback: (match) => {
                    for (const preset of this.presets) {
                        this.socket.send(`preset "${escape(preset.name)}"`);
                    }
                    this.socket.send('presetlist');
                },
            },
            {
                regExp: /^run "(.+)"/,
                callback: (match) => {
                    const name = unescape(match[1]);
                    if (this.getPreset(name) === undefined) {
                        this.socket.send('error "no preset with that name exists"');
                    }
                    else {
                        if (name === 'Mics Mute Preset') {
                            Sutl.setProp(this.getChannel('Mics'), 'mute', true);
                            this.socket.send('val mute "Mics" 1');
                        }
                        else if (name === 'Mics Unmute Preset') {
                            Sutl.setProp(this.getChannel('Mics'), 'mute', false);
                            this.socket.send('val mute "Mics" 0');
                        }
                        else if (name === 'Sound Reinforcement Mute Preset') {
                            Sutl.setProp(this.getChannel('Sound Reinforcement'), 'mute', true);
                            Sutl.setProp(this.getChannel('Sound Reinforcement Mute Status'), 'mute', true);
                            this.socket.send('val mute "Sound Reinforcement" 1');
                            this.socket.send('val digital_gpio_state "Sound Reinforcement Mute Status" 1');
                        }
                        else if (name === 'Sound Reinforcement Unmute Preset') {
                            Sutl.setProp(this.getChannel('Sound Reinforcement'), 'mute', false);
                            Sutl.setProp(this.getChannel('Sound Reinforcement Mute Status'), 'mute', false);
                            this.socket.send('val mute "Sound Reinforcement" 0');
                            this.socket.send('val digital_gpio_state "Sound Reinforcement Mute Status" 0');
                        }
                        this.socket.send(`ran "${match[1]}`);
                    }
                },
            },
            {
                regExp: /^vcglist/,
                callback: (match) => {
                    for (const chan of this.channels) {
                        if (isVirtualChannelGroup(chan)) {
                            const name = escape(chan.name);
                            const chans = chan.virtualChannels.map(c => `"${escape(c)}"`).join(' ');
                            this.socket.send(`vcgitem "${name}" ${chans}`);
                        }
                    }
                    this.socket.send(`vcglist`);
                },
            },
            {
                regExp: /^vclist/,
                callback: (match) => {
                    for (const chan of this.channels) {
                        if (isVirtualChannel(chan)) {
                            const name = escape(chan.name);
                            const vcType = chan.virtualChannelType;
                            const pcType = chan.physicalChannelType;
                            const pcIndices = chan.physicalChannelIndices.join(' ');
                            this.socket.send(`vcitem "${name}" ${vcType} ${pcType} ${pcIndices}`);
                        }
                    }
                    this.socket.send('vclist');
                },
            },
            {
                regExp: /^get voip_call_appearance_info "(.+)" (\d+) (\d+)/,
                callback: (match) => {
                    const name = unescape(match[1]);
                    const vc = this.getVirtualChannel(name, 'voip_out');
                    if (vc === undefined)
                        return;
                    const info = escape(vc.voipCallAppearanceInfo[parseInt(match[3]) - 1]);
                    this.socket.send(`val voip_call_appearance_info "${match[1]}" ${match[2]} ${match[3]} "${info}"`);
                },
            },
            {
                regExp: /^get voip_call_appearance_state "(.+)" (\d+)/,
                callback: (match) => {
                    const name = unescape(match[1]);
                    const vc = this.getVirtualChannel(name, 'voip_out');
                    if (vc === undefined)
                        return;
                    const state = vc.voipCallAppearanceState;
                    this.socket.send(`val voip_call_appearance_state "${match[1]}" ${match[2]} ${state}`);
                },
            },
            {
                regExp: /^get voip_line_state "(.+)" (\d+)/,
                callback: (match) => {
                    const name = unescape(match[1]);
                    const vc = this.getVirtualChannel(name, 'voip_out');
                    if (vc === undefined)
                        return;
                    const state = vc.voipLineState;
                    this.socket.send(`val voip_line_state "${match[1]}" ${match[2]} ${state}`);
                },
            },
            {
                regExp: /^get ye_flask/,
                callback: (match) => {
                    this.socket.send('val ye_flask "You can\'t get ye flask!"');
                },
            },
            {
                regExp: /.+/,
                callback: (match) => {
                    this.socket.send('error "invalid action specified"');
                },
            },
        ];
    }
    onDisconnect() {
        this.timeouts.clearAll();
    }
    getChannel(name) {
        for (const chan of this.channels) {
            if (chan.name === name)
                return chan;
        }
        return undefined;
    }
    getVirtualChannel(name, types) {
        const vc = this.getChannel(name);
        if (vc === undefined) {
            this.socket.send('error "no virtual channel or virtual channel group with that label exists"');
            return undefined;
        }
        if (!isVirtualChannel(vc))
            return undefined;
        if (types === undefined)
            return vc;
        if (!Array.isArray(types))
            types = [types];
        if (!types.includes(vc.physicalChannelType)) {
            this.socket.send('error "the specified channel is not supported by this parameter"');
            return undefined;
        }
        return vc;
    }
    getPreset(name) {
        for (const preset of this.presets) {
            if (preset.name === name)
                return preset;
        }
        return undefined;
    }
}
SoundStructure.emulatorType = 'socketEmulator';
SoundStructure.key = '-KD9NCAlvlhHbip4c4Eg';
